import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
    Box, Button, CircularProgress, TextField, Typography,
} from '@mui/material';
import { Folder as FolderIcon, FileText, Lock, MailCheck, AlertCircle, Clock, ChevronRight, Download } from 'lucide-react';
import configJson from '../api/config.json';
import { ViewerShell, CenteredCard } from '../components/viewer-shell';

type Env = 'development' | 'production';
const config = configJson as Record<Env, { protocol: string; domain: string; port: number }>;

const getApiBase = (): string => {
    const env = (process.env.REACT_APP_ENV || 'production') as Env;
    if (env === 'development') {
        const { protocol, domain, port } = config[env];
        const isDefault = (protocol === 'http' && port === 80) || (protocol === 'https' && port === 443);
        return isDefault ? `${protocol}://${domain}` : `${protocol}://${domain}:${port}`;
    }
    const { protocol, domain } = config.production;
    return `${protocol}://${domain}`;
};

const API_BASE = getApiBase();
const tokenKey = (slug: string) => `folder_token_${slug}`;

type ViewState = 'loading' | 'not_found' | 'expired' | 'access_required' | 'email_sent' | 'access_error' | 'viewing';

interface AccessInfo {
    title: string;
    accessType: 'anonymous' | 'email_list';
    isExpired: boolean;
}

interface FolderNode { id: number; name: string; parentFolderId: number | null; }
interface DocumentNode { id: number; slug: string; title: string; folderId: number | null; createdAt: string; }

interface SharedFolderBrowserProps {
    slug: string;
}

const SharedFolderBrowser: React.FC<SharedFolderBrowserProps> = ({ slug }) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [viewState, setViewState] = useState<ViewState>('loading');
    const [accessInfo, setAccessInfo] = useState<AccessInfo | null>(null);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [errorMsg, setErrorMsg] = useState('');

    const [rootFolderId, setRootFolderId] = useState<number | null>(null);
    const [rootFolderName, setRootFolderName] = useState('');
    const [folders, setFolders] = useState<FolderNode[]>([]);
    const [documents, setDocuments] = useState<DocumentNode[]>([]);
    const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
    const [contentsError, setContentsError] = useState('');

    const [email, setEmail] = useState('');
    const [requestingAccess, setRequestingAccess] = useState(false);
    const [emailError, setEmailError] = useState('');

    // ─── Resolve access token on mount ──────────────────────────────────────

    useEffect(() => {
        const urlToken = searchParams.get('accessToken');
        const urlError = searchParams.get('error');

        if (urlToken) {
            sessionStorage.setItem(tokenKey(slug), urlToken);
            setAccessToken(urlToken);
            setSearchParams({}, { replace: true });
        } else {
            const stored = sessionStorage.getItem(tokenKey(slug));
            if (stored) setAccessToken(stored);
        }

        if (urlError) {
            const messages: Record<string, string> = {
                invalid_token: 'This verification link is invalid or has already been used.',
                expired: 'This folder link has expired.',
                access_revoked: 'Your access to this folder has been revoked.',
                missing_params: 'The verification link is missing required parameters.',
                document_not_found: 'Folder not found.',
                server_error: 'Something went wrong. Please try again.',
            };
            setErrorMsg(messages[urlError] || 'An error occurred.');
            setViewState('access_error');
            setSearchParams({}, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── Fetch access info ────────────────────────────────────────────────────

    useEffect(() => {
        if (viewState === 'access_error') return;

        const token = accessToken || sessionStorage.getItem(tokenKey(slug));
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;

        axios.get(`${API_BASE}/document-folders/by-slug/${slug}/access`, { headers })
            .then(({ data }) => {
                const info: AccessInfo = { title: data.title, accessType: data.accessType, isExpired: data.isExpired };
                setAccessInfo(info);

                if (!data.success || data.isExpired) {
                    setViewState(data.isExpired ? 'expired' : 'not_found');
                    return;
                }

                if (info.accessType === 'anonymous') {
                    setViewState('viewing');
                    return;
                }

                const currentToken = accessToken || sessionStorage.getItem(tokenKey(slug));
                setViewState(currentToken ? 'viewing' : 'access_required');
            })
            .catch(err => {
                if (err.response?.status === 404) {
                    setViewState('not_found');
                } else {
                    setErrorMsg('Failed to load folder.');
                    setViewState('access_error');
                }
            });
    }, [slug, accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Fetch contents ───────────────────────────────────────────────────────

    useEffect(() => {
        if (viewState !== 'viewing') return;
        const token = accessToken || sessionStorage.getItem(tokenKey(slug));
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;

        axios.get(`${API_BASE}/document-folders/by-slug/${slug}/contents`, { headers })
            .then(({ data }) => {
                setRootFolderId(data.rootFolderId);
                setRootFolderName(data.rootFolderName);
                setFolders(data.folders || []);
                setDocuments(data.documents || []);
                setCurrentFolderId(data.rootFolderId);
            })
            .catch(() => setContentsError('Unable to load this folder. Your access may have been revoked.'));
    }, [slug, viewState, accessToken]);

    // ─── Request verification email ──────────────────────────────────────────

    const handleRequestAccess = async () => {
        if (!email.trim()) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setEmailError('Please enter a valid email address.');
            return;
        }
        setEmailError('');
        setRequestingAccess(true);
        try {
            await axios.post(`${API_BASE}/document-folders/by-slug/${slug}/request-access`, { email: email.trim().toLowerCase() });
            setViewState('email_sent');
        } catch (err: any) {
            setEmailError(err.response?.data?.error || 'Failed to send verification email.');
        } finally {
            setRequestingAccess(false);
        }
    };

    // ─── Navigation helpers ────────────────────────────────────────────────────

    const breadcrumbs = React.useMemo(() => {
        const byId = new Map(folders.map(f => [f.id, f]));
        const trail: { id: number; name: string }[] = [];
        let current = currentFolderId ? byId.get(currentFolderId) : undefined;
        let guard = 0;
        while (current && current.id !== rootFolderId && guard < 10) {
            trail.unshift({ id: current.id, name: current.name });
            current = current.parentFolderId ? byId.get(current.parentFolderId) : undefined;
            guard++;
        }
        return trail;
    }, [folders, currentFolderId, rootFolderId]);

    const childFolders = folders.filter(f => f.parentFolderId === currentFolderId && f.id !== rootFolderId).sort((a, b) => a.name.localeCompare(b.name));
    const childDocuments = documents.filter(d => d.folderId === currentFolderId).sort((a, b) => a.title.localeCompare(b.title));

    const downloadUrl = (docSlug: string) => {
        const token = accessToken || sessionStorage.getItem(tokenKey(slug));
        return `${API_BASE}/documents/${docSlug}/file${token ? `?accessToken=${encodeURIComponent(token)}` : ''}`;
    };

    // ─── Render states ────────────────────────────────────────────────────────

    if (viewState === 'loading') {
        return (
            <ViewerShell title="" icon="folder">
                <CenteredCard>
                    <CircularProgress sx={{ color: 'var(--color-primary)' }} />
                    <Typography sx={{ mt: 2, color: 'var(--color-text-muted)' }}>Loading folder…</Typography>
                </CenteredCard>
            </ViewerShell>
        );
    }

    if (viewState === 'not_found') {
        return (
            <ViewerShell title="Not Found" icon="folder">
                <CenteredCard>
                    <FolderIcon size={48} color="#ccc" />
                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>Folder not found</Typography>
                    <Typography sx={{ color: 'var(--color-text-muted)', mt: 1 }}>This link may be invalid or the folder is no longer shared.</Typography>
                </CenteredCard>
            </ViewerShell>
        );
    }

    if (viewState === 'expired') {
        return (
            <ViewerShell title="Link Expired" icon="folder">
                <CenteredCard>
                    <Clock size={48} color="#ccc" />
                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>This link has expired</Typography>
                    <Typography sx={{ color: 'var(--color-text-muted)', mt: 1 }}>The folder owner has set an expiry date on this link.</Typography>
                </CenteredCard>
            </ViewerShell>
        );
    }

    if (viewState === 'access_error') {
        return (
            <ViewerShell title="Access Error" icon="folder">
                <CenteredCard>
                    <AlertCircle size={48} color="#e53935" />
                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>Access denied</Typography>
                    <Typography sx={{ color: 'var(--color-text-muted)', mt: 1, textAlign: 'center', maxWidth: 320 }}>{errorMsg}</Typography>
                    {accessInfo?.accessType === 'email_list' && (
                        <Button
                            variant="outlined"
                            sx={{ mt: 3, borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }}
                            onClick={() => { setErrorMsg(''); setViewState('access_required'); }}
                        >
                            Try again
                        </Button>
                    )}
                </CenteredCard>
            </ViewerShell>
        );
    }

    if (viewState === 'access_required') {
        return (
            <ViewerShell title={accessInfo?.title || 'Restricted Folder'} icon="folder">
                <CenteredCard>
                    <Lock size={48} color="var(--color-primary)" />
                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 700 }}>{accessInfo?.title || 'Restricted Folder'}</Typography>
                    <Typography sx={{ color: 'var(--color-text-muted)', mt: 1, mb: 3, textAlign: 'center', maxWidth: 340 }}>
                        This folder is restricted. Enter the email address you were invited with and we'll send you a verification link.
                    </Typography>
                    <Box sx={{ width: '100%', maxWidth: 340 }}>
                        <TextField
                            fullWidth size="small" type="email" placeholder="you@example.com"
                            value={email}
                            onChange={e => { setEmail(e.target.value); setEmailError(''); }}
                            onKeyDown={e => e.key === 'Enter' && handleRequestAccess()}
                            error={!!emailError}
                            helperText={emailError}
                            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <Button
                            fullWidth variant="contained"
                            onClick={handleRequestAccess}
                            disabled={requestingAccess || !email.trim()}
                            startIcon={requestingAccess ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : undefined}
                            sx={{ background: 'var(--color-primary)', borderRadius: 2, boxShadow: 'none', py: 1.2, '&:hover': { background: 'var(--color-primary-hover)' } }}
                        >
                            {requestingAccess ? 'Sending…' : 'Send verification link'}
                        </Button>
                    </Box>
                </CenteredCard>
            </ViewerShell>
        );
    }

    if (viewState === 'email_sent') {
        return (
            <ViewerShell title="Check your email" icon="folder">
                <CenteredCard>
                    <MailCheck size={48} color="var(--color-primary)" />
                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 700 }}>Check your email</Typography>
                    <Typography sx={{ color: 'var(--color-text-muted)', mt: 1, textAlign: 'center', maxWidth: 340 }}>
                        We've sent a verification link to <strong>{email}</strong>. Click it to open the folder.
                    </Typography>
                    <Button variant="text" sx={{ mt: 3, color: 'var(--color-primary)', textTransform: 'none' }} onClick={() => setViewState('access_required')}>
                        Use a different email
                    </Button>
                </CenteredCard>
            </ViewerShell>
        );
    }

    // ── viewing ──────────────────────────────────────────────────────────────
    return (
        <ViewerShell title={accessInfo?.title || rootFolderName} icon="folder">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 3, py: 1.5, borderBottom: '1px solid #eee', flexShrink: 0 }}>
                <Typography
                    onClick={() => setCurrentFolderId(rootFolderId)}
                    sx={{
                        fontSize: 13, fontWeight: currentFolderId === rootFolderId ? 700 : 500,
                        color: currentFolderId === rootFolderId ? 'var(--color-default-text)' : 'var(--color-primary)',
                        cursor: currentFolderId === rootFolderId ? 'default' : 'pointer',
                        '&:hover': currentFolderId === rootFolderId ? {} : { textDecoration: 'underline' },
                    }}
                >
                    {rootFolderName}
                </Typography>
                {breadcrumbs.map((b, idx) => (
                    <React.Fragment key={b.id}>
                        <ChevronRight size={14} color="var(--color-text-muted)" />
                        <Typography
                            onClick={() => setCurrentFolderId(b.id)}
                            sx={{
                                fontSize: 13, fontWeight: idx === breadcrumbs.length - 1 ? 700 : 500,
                                color: idx === breadcrumbs.length - 1 ? 'var(--color-default-text)' : 'var(--color-primary)',
                                cursor: idx === breadcrumbs.length - 1 ? 'default' : 'pointer',
                                '&:hover': idx === breadcrumbs.length - 1 ? {} : { textDecoration: 'underline' },
                            }}
                        >
                            {b.name}
                        </Typography>
                    </React.Fragment>
                ))}
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                {contentsError ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8, gap: 1 }}>
                        <AlertCircle size={32} color="#e53935" />
                        <Typography sx={{ color: 'var(--color-text-muted)' }}>{contentsError}</Typography>
                    </Box>
                ) : rootFolderId === null ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 8, justifyContent: 'center' }}>
                        <CircularProgress size={20} sx={{ color: 'var(--color-primary)' }} />
                        <Typography sx={{ color: 'var(--color-text-muted)' }}>Loading contents…</Typography>
                    </Box>
                ) : childFolders.length === 0 && childDocuments.length === 0 ? (
                    <Typography sx={{ color: 'var(--color-text-muted)', textAlign: 'center', mt: 8 }}>
                        This folder is empty.
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxWidth: 720, mx: 'auto' }}>
                        {childFolders.map(folder => (
                            <Box
                                key={`f-${folder.id}`}
                                onClick={() => setCurrentFolderId(folder.id)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, cursor: 'pointer',
                                    background: 'var(--color-page-background)', border: '1px solid #eee',
                                    '&:hover': { borderColor: 'var(--color-primary)' },
                                }}
                            >
                                <FolderIcon size={18} color="var(--color-primary)" />
                                <Typography sx={{ fontSize: 14, fontWeight: 600, flex: 1 }}>{folder.name}</Typography>
                                <ChevronRight size={16} color="var(--color-text-muted)" />
                            </Box>
                        ))}
                        {childDocuments.map(doc => (
                            <Box
                                key={`d-${doc.id}`}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2,
                                    background: 'var(--color-page-background)', border: '1px solid #eee',
                                }}
                            >
                                <FileText size={18} color="var(--color-text-muted)" />
                                <Typography sx={{ fontSize: 14, flex: 1 }}>{doc.title}</Typography>
                                <Button
                                    size="small"
                                    href={downloadUrl(doc.slug)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    startIcon={<Download size={14} />}
                                    sx={{ fontSize: 12, textTransform: 'none', color: 'var(--color-primary)' }}
                                >
                                    Open
                                </Button>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </ViewerShell>
    );
};

export default SharedFolderBrowser;
